import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { clsx } from 'clsx'
import reverLogo from '@/assets/images/integrations/rever.svg'
import shopifyLogo from '@/assets/images/integrations/shopify.svg'
import stripeLogo from '@/assets/images/integrations/stripe.svg'
import salesforceLogo from '@/assets/images/integrations/salesforce.svg'
import upsLogo from '@/assets/images/integrations/ups.svg'
import fedexLogo from '@/assets/images/integrations/fedex.svg'
import dhlLogo from '@/assets/images/integrations/dhl.svg'
import paypalLogo from '@/assets/images/integrations/paypal.svg'
import correosLogo from '@/assets/images/integrations/correos.svg'
import klaviyoLogo from '@/assets/images/integrations/klaviyo.svg'
import zendeskLogo from '@/assets/images/integrations/zendesk.svg'

interface OrbitLogo {
  src: string
  // Degrees clockwise from 3 o'clock.
  angle: number
  // A logo drawn as its own tile (DHL's yellow square) fills the white one.
  fill?: boolean
}

const INNER_RADIUS = 88
const OUTER_RADIUS = 142
const EDGE_RADIUS = 202

const INNER: OrbitLogo[] = [
  { src: shopifyLogo, angle: -49 },
  { src: stripeLogo, angle: -169 },
  { src: salesforceLogo, angle: 71 }
]

const OUTER: OrbitLogo[] = [
  { src: upsLogo, angle: -105 },
  { src: fedexLogo, angle: -54 },
  { src: dhlLogo, angle: -2, fill: true },
  { src: paypalLogo, angle: 49 },
  { src: correosLogo, angle: 101 },
  { src: klaviyoLogo, angle: 152 },
  { src: zendeskLogo, angle: -157 }
]

// The inner ring turns clockwise and the outer one counterclockwise; each logo turns back
// at the same speed so it stays upright.
const INNER_SPIN = 'animate-[spin_60s_linear_infinite]'
const INNER_UNSPIN = 'animate-[spin_60s_linear_infinite_reverse]'
const OUTER_SPIN = 'animate-[spin_90s_linear_infinite_reverse]'
const OUTER_UNSPIN = 'animate-[spin_90s_linear_infinite]'

export const IntegrationsBanner = () => {
  const { t } = useTranslation()
  return (
    <Base.Card className="flex items-center overflow-hidden py-8 xl:h-[298px] xl:py-0">
      <div className="flex min-w-0 flex-[5] flex-col gap-3 px-7">
        <p className="text-xs font-medium uppercase tracking-wider text-primary-base">
          {t('integrations_hub.banner.eyebrow')}
        </p>
        <h2 className="font-roobert text-2xl font-medium text-neutral-900">
          {t('integrations_hub.banner.title')}
        </h2>
        <p className="max-w-64 text-sm leading-relaxed text-neutral-600">
          {t('integrations_hub.banner.description')}
        </p>
      </div>
      {/* Its own column, shown only from xl: below it the card is too narrow for copy and rings. */}
      <div
        aria-hidden="true"
        className="relative hidden flex-[6] self-stretch xl:block"
      >
        <div className="absolute inset-y-0 left-1/2 w-0">
          <Ring radius={EDGE_RADIUS} dashed />
          <Ring radius={OUTER_RADIUS} />
          <Ring radius={INNER_RADIUS} />
          <Orbit
            logos={OUTER}
            radius={OUTER_RADIUS}
            spin={OUTER_SPIN}
            unspin={OUTER_UNSPIN}
          />
          <Orbit
            logos={INNER}
            radius={INNER_RADIUS}
            spin={INNER_SPIN}
            unspin={INNER_UNSPIN}
          />
          <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-blue-100/70 p-2 shadow-lg shadow-blue-200/60">
            <img
              src={reverLogo}
              alt=""
              className="size-14 max-w-none rounded-2xl"
            />
          </div>
        </div>
      </div>
    </Base.Card>
  )
}

const Ring = ({ radius, dashed }: { radius: number; dashed?: boolean }) => (
  <div
    className={clsx(
      'absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border',
      dashed
        ? 'border-dashed border-neutral-300'
        : 'border-solid border-neutral-200'
    )}
    style={{ width: radius * 2, height: radius * 2 }}
  />
)

const Orbit = ({
  logos,
  radius,
  spin,
  unspin
}: {
  logos: OrbitLogo[]
  radius: number
  spin: string
  unspin: string
}) => (
  <div className="absolute left-0 top-1/2 size-0">
    <div className={clsx('absolute inset-0 motion-reduce:animate-none', spin)}>
      {logos.map(({ src, angle, fill }) => {
        const radians = (angle * Math.PI) / 180
        return (
          <div
            key={src}
            className="absolute -ml-4 -mt-4"
            style={{
              left: radius * Math.cos(radians),
              top: radius * Math.sin(radians)
            }}
          >
            <div
              className={clsx(
                'flex size-8 items-center justify-center rounded-lg border border-solid border-neutral-100 bg-white shadow-sm motion-reduce:animate-none',
                unspin
              )}
            >
              <img
                src={src}
                alt=""
                className={
                  fill
                    ? 'size-7 max-w-none rounded-md'
                    : 'size-5 max-w-none object-contain'
                }
              />
            </div>
          </div>
        )
      })}
    </div>
  </div>
)
